
import { useState, useEffect } from "react";
import useSummitSync from "./useSummitSync";
import { SaveStatus } from "./SummitCaptureTabs";

const C = {
  dark: "#0f1923", navy: "#1a2633", border: "#2a3a4a",
  gold: "#c9a84c", green: "#2ecc71", orange: "#f39c12",
  red: "#e74c3c", muted: "#6b8299", white: "#e8edf2"
};

const DEFAULT = {
  // Infrastructure (annual)
  fivetran: 20000,
  snowflake: 40000,
  dbt: 25000,
  quicksight: 40000,
  // MSP sprint cost (12-week base)
  mspBase: 250000,
  // Internal staff aug cost per month of acceleration
  staffAugPerMonth: 25000,
  // Risk factors (1-5 scale, 5 = highest risk)
  dataQuality: 3,
  martinBandwidth: 4,
  mspRampTime: 3,
  vendorDecisionSpeed: 3,
  changeManagement: 2,
};

const SCENARIOS = [
  { id: "A", label: "Baseline",    months: 0,  color: C.green  },
  { id: "B", label: "+1 Month",    months: 1,  color: "#27ae60" },
  { id: "C", label: "+2 Months",   months: 2,  color: C.orange },
  { id: "D", label: "+3 Months",   months: 3,  color: "#e67e22" },
  { id: "E", label: "+4 Months",   months: 4,  color: C.red    },
];

function calcScenario(s, v) {
  const infraAnnual = v.fivetran + v.snowflake + v.dbt + v.quicksight;
  const infraMonthly = infraAnnual / 12;
  // Acceleration cost: MSP base compressed + staff aug per saved month
  const accelMonths = s.months;
  const mspCost = accelMonths === 0 ? v.mspBase : v.mspBase * (1 + accelMonths * 0.12);
  const staffAug = accelMonths * v.staffAugPerMonth;
  const total = infraAnnual + mspCost + staffAug;
  // Risk score: weighted composite
  const riskFactors = [
    { val: v.dataQuality,         weight: 0.25 },
    { val: v.martinBandwidth,     weight: 0.25 },
    { val: v.mspRampTime,         weight: 0.20 },
    { val: v.vendorDecisionSpeed, weight: 0.15 },
    { val: v.changeManagement,    weight: 0.15 },
  ];
  const baseRisk = riskFactors.reduce((sum, f) => sum + f.val * f.weight, 0);
  // Acceleration amplifies risk: each month adds 8% to base risk score
  const accelRisk = Math.min(5, baseRisk * (1 + accelMonths * 0.08));
  const riskPct = Math.round((accelRisk / 5) * 100);
  return { infraAnnual, mspCost, staffAug, total, riskPct, accelRisk };
}

function fmt(n) {
  return "$" + Math.round(n).toLocaleString();
}

function Thermometer({ pct, color }) {
  const h = 180;
  const fill = Math.round((pct / 100) * h);
  const bulbR = 18;
  const tubeW = 16;
  const label = pct < 30 ? "LOW" : pct < 55 ? "MODERATE" : pct < 75 ? "HIGH" : "CRITICAL";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <svg width={60} height={h + bulbR * 2 + 10} style={{ overflow: "visible" }}>
        {/* Tube background */}
        <rect x={(60 - tubeW) / 2} y={bulbR} width={tubeW} height={h} rx={tubeW / 2}
          fill={C.border} />
        {/* Fill */}
        <rect x={(60 - tubeW) / 2} y={bulbR + (h - fill)} width={tubeW} height={fill}
          rx={tubeW / 2} fill={color} style={{ transition: "height 0.6s ease, y 0.6s ease" }} />
        {/* Bulb */}
        <circle cx={30} cy={h + bulbR + 6} r={bulbR} fill={color} />
        {/* Tick marks */}
        {[0, 25, 50, 75, 100].map(p => (
          <line key={p}
            x1={(60 - tubeW) / 2 - 4} y1={bulbR + h - (p / 100) * h}
            x2={(60 - tubeW) / 2} y2={bulbR + h - (p / 100) * h}
            stroke={C.muted} strokeWidth={1} />
        ))}
      </svg>
      <div style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.white }}>{pct}%</div>
    </div>
  );
}

function Slider({ label, value, onChange, min = 1, max = 5 }) {
  const colors = [C.green, "#27ae60", C.orange, "#e67e22", C.red];
  const c = colors[value - 1];
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: C.white }}>{label}</span>
        <span style={{ color: c, fontWeight: 700 }}>{value}/5</span>
      </div>
      <input type="range" min={min} max={max} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: c }} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.muted }}>
        <span>Low</span><span>High</span>
      </div>
    </div>
  );
}

function NumInput({ label, value, onChange, prefix = "$", suffix = "/yr" }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ color: C.muted, fontSize: 12 }}>{prefix}</span>
        <input type="number" value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{ background: C.dark, border: "1px solid " + C.border, color: C.white,
            borderRadius: 4, padding: "4px 8px", fontSize: 12, width: 90 }} />
        <span style={{ color: C.muted, fontSize: 11 }}>{suffix}</span>
      </div>
    </div>
  );
}

export default function AccelerationCapture() {
  const { data: spData, update: spUpdate, saveStatus, loaded } = useSummitSync("s15_accel", DEFAULT);
  const v = loaded ? { ...DEFAULT, ...spData } : DEFAULT;
  const [selected, setSelected] = useState("C");

  const set = (key, val) => {
    spUpdate({ ...v, [key]: val });
  };

  const results = SCENARIOS.map(s => ({ ...s, ...calcScenario(s, v) }));
  const sel = results.find(r => r.id === selected);

  const thermColor = sel.riskPct < 30 ? C.green
    : sel.riskPct < 55 ? C.orange
    : sel.riskPct < 75 ? "#e67e22"
    : C.red;

  return (
    <div style={{ background: C.dark, minHeight: "100%", padding: 24, color: C.white, fontFamily: "Inter, sans-serif" }}>
      <div style={{ fontSize: 11, color: C.gold, letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>
        ACCELERATION PLANNING
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Cost vs. Risk Calculator</div>
        <SaveStatus status={saveStatus} />
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 24 }}>
        Adjust infrastructure costs, risk factors, and MSP assumptions. Select a scenario to see the full breakdown.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>

        {/* LEFT: Inputs */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Infrastructure costs */}
          <div style={{ background: C.navy, border: "1px solid " + C.border, borderRadius: 8, padding: 16 }}>
            <div style={{ fontSize: 11, color: C.gold, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>Infrastructure Costs (Annual)</div>
            <NumInput label="Fivetran / ETL" value={v.fivetran} onChange={val => set("fivetran", val)} />
            <NumInput label="Snowflake" value={v.snowflake} onChange={val => set("snowflake", val)} />
            <NumInput label="dbt / Transformation" value={v.dbt} onChange={val => set("dbt", val)} />
            <NumInput label="QuickSight / BI" value={v.quicksight} onChange={val => set("quicksight", val)} />
            <div style={{ borderTop: "1px solid " + C.border, marginTop: 10, paddingTop: 10, fontSize: 12 }}>
              <span style={{ color: C.muted }}>Total stack: </span>
              <span style={{ color: C.gold, fontWeight: 700 }}>{fmt(v.fivetran + v.snowflake + v.dbt + v.quicksight)}/yr</span>
            </div>
          </div>

          {/* MSP + Staff Aug */}
          <div style={{ background: C.navy, border: "1px solid " + C.border, borderRadius: 8, padding: 16 }}>
            <div style={{ fontSize: 11, color: C.gold, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>MSP and Staff Augmentation</div>
            <NumInput label="MSP Base Sprint (12 wks)" value={v.mspBase} onChange={val => set("mspBase", val)} suffix="" />
            <NumInput label="Staff Aug per Accel Month" value={v.staffAugPerMonth} onChange={val => set("staffAugPerMonth", val)} suffix="/mo" />
          </div>
        </div>

        {/* MIDDLE: Risk sliders */}
        <div style={{ background: C.navy, border: "1px solid " + C.border, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 11, color: C.gold, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>Risk Factors (1=Low, 5=High)</div>
          <Slider label="Data Quality Readiness" value={v.dataQuality} onChange={val => set("dataQuality", val)} />
          <Slider label="Martin Bandwidth" value={v.martinBandwidth} onChange={val => set("martinBandwidth", val)} />
          <Slider label="MSP Ramp Time" value={v.mspRampTime} onChange={val => set("mspRampTime", val)} />
          <Slider label="Vendor Decision Speed" value={v.vendorDecisionSpeed} onChange={val => set("vendorDecisionSpeed", val)} />
          <Slider label="Change Management" value={v.changeManagement} onChange={val => set("changeManagement", val)} />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 12, lineHeight: 1.5 }}>
            Risk score is weighted across all 5 factors. Each month of acceleration amplifies the composite by 8%.
          </div>
        </div>

        {/* RIGHT: Thermometer for selected scenario */}
        <div style={{ background: C.navy, border: "1px solid " + C.border, borderRadius: 8, padding: 16, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ fontSize: 11, color: C.gold, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>Failure Risk</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Scenario {sel.id}: {sel.label}</div>
          <Thermometer pct={sel.riskPct} color={thermColor} />
          <div style={{ marginTop: 20, width: "100%" }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Risk drivers at this acceleration:</div>
            {[
              { label: "Data quality", val: v.dataQuality },
              { label: "Martin bandwidth", val: v.martinBandwidth },
              { label: "MSP ramp", val: v.mspRampTime },
            ].map(f => (
              <div key={f.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
                <span style={{ color: C.muted }}>{f.label}</span>
                <span style={{ color: f.val >= 4 ? C.red : f.val >= 3 ? C.orange : C.green }}>{"▓".repeat(f.val)}{"░".repeat(5 - f.val)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scenario comparison table */}
      <div style={{ marginTop: 20, background: C.navy, border: "1px solid " + C.border, borderRadius: 8, padding: 16 }}>
        <div style={{ fontSize: 11, color: C.gold, letterSpacing: 1, textTransform: "uppercase", marginBottom: 16 }}>Scenario Comparison</div>
        <div style={{ display: "grid", gridTemplateColumns: "80px repeat(5, 1fr)", gap: 0 }}>
          {/* Header */}
          {["", ...SCENARIOS.map(s => s.label)].map((h, i) => (
            <div key={i} style={{ fontSize: 11, color: C.muted, padding: "6px 10px", textAlign: i === 0 ? "left" : "center",
              borderBottom: "1px solid " + C.border }}>{h}</div>
          ))}
          {/* Rows */}
          {[
            { label: "Infra",    key: "infraAnnual" },
            { label: "MSP",      key: "mspCost" },
            { label: "Staff Aug",key: "staffAug" },
            { label: "TOTAL",    key: "total", bold: true },
            { label: "Risk",     key: "riskPct", isRisk: true },
          ].map(row => (
            <>
              <div key={row.label} style={{ fontSize: 12, color: row.bold ? C.white : C.muted,
                padding: "8px 10px", fontWeight: row.bold ? 700 : 400,
                borderBottom: "1px solid " + C.border }}>{row.label}</div>
              {results.map(r => (
                <div key={r.id}
                  onClick={() => setSelected(r.id)}
                  style={{
                    fontSize: 12, textAlign: "center", padding: "8px 4px", cursor: "pointer",
                    fontWeight: row.bold ? 700 : 400,
                    borderBottom: "1px solid " + C.border,
                    background: selected === r.id ? "rgba(201,168,76,0.08)" : "transparent",
                    color: row.isRisk
                      ? (r.riskPct < 30 ? C.green : r.riskPct < 55 ? C.orange : r.riskPct < 75 ? "#e67e22" : C.red)
                      : (row.bold ? r.color : C.white)
                  }}>
                  {row.isRisk ? r.riskPct + "%" : fmt(r[row.key])}
                </div>
              ))}
            </>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
          <div style={{ fontSize: 11, color: C.muted }}>Click any column to select that scenario and update the thermometer.</div>
          <button
            onClick={() => {
              const rows = [
                ["Scenario", "Label", "Infra Annual", "MSP Cost", "Staff Aug", "Total", "Risk %"],
                ...results.map(r => [r.id, r.label, r.infraAnnual, Math.round(r.mspCost), r.staffAug, Math.round(r.total), r.riskPct]),
                [""],
                ["Input", "Value"],
                ["Fivetran/yr", v.fivetran],
                ["Snowflake/yr", v.snowflake],
                ["dbt/yr", v.dbt],
                ["QuickSight/yr", v.quicksight],
                ["MSP Base Sprint", v.mspBase],
                ["Staff Aug/mo", v.staffAugPerMonth],
                ["Risk: Data Quality", v.dataQuality],
                ["Risk: Martin Bandwidth", v.martinBandwidth],
                ["Risk: MSP Ramp Time", v.mspRampTime],
                ["Risk: Vendor Decision Speed", v.vendorDecisionSpeed],
                ["Risk: Change Management", v.changeManagement],
              ];
              const csv = rows.map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(",")).join("\n");
              const a = document.createElement("a");
              a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
              a.download = "TCC_Acceleration_Plan.csv";
              a.click();
            }}
            style={{ padding: "6px 14px", background: C.gold, color: C.dark, border: "none",
              borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Export CSV
          </button>
        </div>
      </div>
    </div>
  );
}
